// Moves a client's excess receipts against its overdue invoices (manual, with a
// note). Stored as one document per (excess invoice -> overdue invoice) pair,
// grouped by the action that created them so a whole adjustment can be reversed
// at once. The AR register reads these (arRegisterService.js) — nothing else
// changes, because the money was already received.
import { collection, doc, getDocs, query, serverTimestamp, where, writeBatch } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { buildAccountsReceivableRows } from "./arRegisterService";
import { excessSources, overdueTargets, planTransfers, poolOf, validateExcessAdjustment } from "./excessAdjustment";

const ADJUSTMENTS = "ar_credit_adjustments";

export async function listCreditAdjustments(clientCode) {
  const result = await getDocs(query(collection(db, ADJUSTMENTS), where("clientCode", "==", clientCode)));
  return result.docs.map((item) => ({ id: item.id, ...item.data() }));
}

// amounts: { [overdueSnapshotId]: amount }. Re-reads the receivables first so the
// excess and the overdue balances are checked against the latest figures.
export async function createExcessAdjustment({ clientCode, amounts, note, date }) {
  const rows = await buildAccountsReceivableRows(clientCode);
  const sources = excessSources(rows);
  const targets = overdueTargets(rows);
  const problem = validateExcessAdjustment({ amounts, targets, pool: poolOf(sources), note, date });
  if (problem) throw new Error(problem);

  const cleanAmounts = Object.fromEntries(Object.entries(amounts).filter(([, value]) => Math.round(Number(value)) > 0).map(([id, value]) => [id, Math.round(Number(value))]));
  const pairs = planTransfers({ sources, amounts: cleanAmounts });
  const groupId = doc(collection(db, ADJUSTMENTS)).id;
  const who = { uid: auth.currentUser?.uid || null, email: auth.currentUser?.email || null };
  const batch = writeBatch(db);
  pairs.forEach((pair) => {
    const target = targets.find((item) => item.id === pair.toSnapshotId);
    batch.set(doc(collection(db, ADJUSTMENTS)), {
      clientCode, groupId, ...pair, toInvoiceNumber: target.invoiceNumber,
      adjustmentDate: date, note: String(note).trim(), reversed: false,
      createdBy: who.uid, createdByEmail: who.email, createdAt: serverTimestamp()
    });
  });
  await batch.commit();
  return { groupId, pairs, total: pairs.reduce((sum, pair) => sum + pair.amount, 0) };
}

export async function reverseExcessAdjustment(clientCode, groupId, reason) {
  if (!String(reason || "").trim()) throw new Error("Enter a reason for reversing this adjustment.");
  const all = await listCreditAdjustments(clientCode);
  const inGroup = all.filter((item) => item.groupId === groupId && !item.reversed);
  if (!inGroup.length) throw new Error("This adjustment was already reversed.");
  const batch = writeBatch(db);
  inGroup.forEach((item) => batch.update(doc(db, ADJUSTMENTS, item.id), {
    reversed: true, reversalReason: String(reason).trim(), reversedAt: serverTimestamp(), reversedByEmail: auth.currentUser?.email || null
  }));
  await batch.commit();
}
